
import { ViewType, ViewObject } from '../base';

import { dataService } from '../../data';
import { EmployeePermission } from '../objects/employee';

import { User } from '../objects/user';
import { EmployeeView } from './employee';
import { PlaceView } from './place';

/****************************/
/* CLASS VIEW               */
/****************************/

class _UserView extends ViewObject<User> {
    
    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get user(): User{
        return this.object;
    }

    constructor(object: User, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION       */
    /**********************************/
    
    private _isactivesession: boolean | null = null;

    private RefreshView(targets: string[]){
        if (targets.includes('USER')){
            this.DoRefreshView();
        }

        if (targets.includes('SESSION')){
            this._isactivesession = null;
            this.DoRefreshView();
        }

        if (targets.includes('PLACE')){
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void {
        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

        // register to updates on contained items          
        this._subscriptions.push(
            this.user.OnRefresh.subscribe(
            (targets: string[]) => {
                this.RefreshView(targets);
            })
        );        
    }
    
    /**********************************/
    /* ACCESS TO MEMBERS              */
    /**********************************/

    get employees() : Array <EmployeeView> {
        const _employees: Array <EmployeeView> = [];
        for(const _employee of this.user.employees){
            const _employeeview = this.alives.get(_employee) as EmployeeView;
            if (_employeeview && _employeeview.IsValid){
                _employees.push(_employeeview);
            }
        }
        return _employees;
    }
    
    get places() : Array <PlaceView> {
        const _places: Array <PlaceView> = [];
        
        // user owned places 
        for(const _place of this.user.places){
            const _placeview = this.alives.get(_place) as PlaceView;
            if (_placeview && _placeview.IsValid){
                _places.push(_placeview);
            }
        }

        // staff places
        for (const _employee of this.user.employees){
            const _placeview = this.alives.get(_employee.place) as PlaceView;
            if (_placeview && _placeview.IsValid){
                _places.push(_placeview);
            }
        }

        return _places;
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/

    get IsPending(): boolean {
        return this.user.status === 'PA';
    }

    get IsActive(): boolean {
        if (this._isactivesession === null){
            let _active = false;

            for(const _session of this.user.sessions){
                if (_session.IsActive){
                    _active = true;
                }
            }

            this._isactivesession = _active;
        }

        return this._isactivesession;
    }

    get IsPlaceOwner(): boolean {
        if (!this.data.place){
            return false;
        }

        if (this.user == this.data.place.user){
            return true;
        }

        return false;
    } 

    IsAllowedTo(permission: EmployeePermission): boolean {
        // check if this user is the owner of the place
        if (this.IsPlaceOwner){
            return true;
        }

        // check if this user is an employee of the place
        for (const _employee of this.employees){
            if (_employee.place == this.data.place){
                return _employee.IsAllowedTo(permission);
            }
        }

        // not the place owner and not an employee
        return  false;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type UserView = ViewType <_UserView, User>;

export function UserViewProxy(object: User, data: dataService): UserView {
    return new _UserView(object, data).Proxy as UserView;
}